import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  listDevices,
  createDevice,
  reissuePairingCode,
  revokeDevice,
  issuePunchQr,
  listPunchQrs,
  KioskDevice,
  PunchQrEntry,
} from '../services/kioskService';
import QrCode from '../components/QrCode';
import { useAuth } from '../contexts/AuthContext';

/** 名刺サイズの1枚あたり枚数(A4縦 2列×5段)。設計書 §13-8 */
const CARDS_PER_SHEET = 10;

/**
 * 管理画面「打刻用共用端末」。ここでやることは2つ。
 *
 *   1. 端末の登録・失効(ペアリングコードの発行)
 *   2. 従業員の打刻用QRの発行と印刷
 *
 * ⚠️ **ペアリングコードは発行直後の一度しか画面に出ない**(ハッシュでしか保持しないため)。
 *
 * 一方 **QRは何度でも再表示・再印刷できる**。ここは意図的に分けている——
 * 印刷し直すたびに再発行が必要だと、**配布済みのカードがそのたびに死ぬ**からだ。
 * 「印刷」は何度でも安全な操作、「再発行」は配布済みを無効にする破壊的操作、
 * として画面上も明確に区別する。
 */
const KioskDevicesPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  /** 失効した端末も一覧に出すか。既定は隠す（運用中の端末が読み取れなくなるため） */
  const [showRevoked, setShowRevoked] = useState(false);
  const [revokedCount, setRevokedCount] = useState(0);
  const [devices, setDevices] = useState<KioskDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [newName, setNewName] = useState('');
  /** 発行直後のコード。画面を離れると二度と見られない */
  const [issuedCode, setIssuedCode] = useState<{ name: string; code: string } | null>(null);

  // --- 打刻用QR ---
  const [qrs, setQrs] = useState<PunchQrEntry[]>([]);
  const [issuingFor, setIssuingFor] = useState<number | null>(null);
  /** 印刷対象。既定は発行済み全員 */
  const [printIds, setPrintIds] = useState<Set<number>>(new Set());

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [d, q] = await Promise.all([listDevices(showRevoked), listPunchQrs()]);
      setDevices(d.devices);
      setRevokedCount(d.revokedCount);
      setQrs(q);
      // 発行済みは既定で印刷対象にしておく（配り直しが最も多い操作のため）
      setPrintIds(new Set(q.filter((x) => x.qrToken).map((x) => x.userId)));
    } catch (e: any) {
      setError(e.response?.data?.error || '読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, [showRevoked]);

  useEffect(() => { load(); }, [load]);


  const addDevice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      const r = await createDevice(newName.trim());
      setIssuedCode({ name: r.device.name, code: r.pairingCode });
      setNewName('');
      await load();
    } catch (e: any) {
      setError(e.response?.data?.error || '端末の登録に失敗しました');
    }
  };

  const reissue = async (d: KioskDevice) => {
    if (!window.confirm(`「${d.name}」のコードを再発行しますか？\nこの端末の現在の登録は無効になります。`)) return;
    try {
      const r = await reissuePairingCode(d.id);
      setIssuedCode({ name: r.device.name, code: r.pairingCode });
      await load();
    } catch (e: any) {
      setError(e.response?.data?.error || 'コードの再発行に失敗しました');
    }
  };

  const revoke = async (d: KioskDevice) => {
    if (!window.confirm(`「${d.name}」を失効させますか？\nこの端末から打刻できなくなります。`)) return;
    try {
      await revokeDevice(d.id);
      await load();
    } catch (e: any) {
      setError(e.response?.data?.error || '端末の失効に失敗しました');
    }
  };

  /**
   * QRの発行。**未発行なら確認なし、再発行は強い確認を挟む。**
   * 再発行はその人の配布済みQRを全て無効にする破壊的操作なので、
   * 何が起きるかを具体的に書いてから聞く。
   */
  const issueQr = async (entry: PunchQrEntry) => {
    if (entry.qrToken) {
      const ok = window.confirm(
        `【再発行】${entry.name} さんのQRコードを作り直します。\n\n` +
        `・いま配布しているカードや、本人のスマホに表示されるQRは\n` +
        `  この操作の直後から使えなくなります\n` +
        `・新しいQRを印刷して配り直す必要があります\n\n` +
        `印刷し直したいだけであれば、再発行は不要です\n` +
        `（下の「印刷」で現在のQRをそのまま刷れます）。\n\n` +
        `紛失・複製の疑いがある場合にのみ実行してください。続けますか？`
      );
      if (!ok) return;
    }
    setIssuingFor(entry.userId);
    try {
      await issuePunchQr(entry.userId);
      await load();
    } catch (e: any) {
      setError(e.response?.data?.error || 'QRコードの発行に失敗しました');
    } finally {
      setIssuingFor(null);
    }
  };

  const togglePrint = (userId: number) => {
    setPrintIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  /**
   * 印刷対象の全選択・全解除。
   *
   * **一人ずつ外すより、全部外してから要る人だけ入れる方が速い**場面がある
   * (入社1名ぶんだけ刷り直す等)。既定が「発行済み全員」なので、
   * その逆をワンタッチでできないと詰む。
   *
   * 未発行者は印刷しようがないので対象に含めない。
   */
  const toggleAllPrint = () => {
    setPrintIds((prev) => {
      const issuedIds = qrs.filter((q) => q.qrToken).map((q) => q.userId);
      const allOn = issuedIds.length > 0 && issuedIds.every((id) => prev.has(id));
      return allOn ? new Set<number>() : new Set(issuedIds);
    });
  };

  const printQrs = () => window.print();

  const fmt = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString('ja-JP', { dateStyle: 'short', timeStyle: 'short' }) : '—';

  if (loading) return <div style={{ padding: 20, textAlign: 'center' }}>読み込み中...</div>;

  const issued = qrs.filter((q) => q.qrToken);
  const printList = issued.filter((q) => printIds.has(q.userId));
  /** 発行済みが全員選ばれているか。ヘッダのチェックボックスの状態 */
  const allPrintSelected = issued.length > 0 && printList.length === issued.length;
  /** 一部だけ選ばれている。チェックとも未チェックとも違う第三の状態を出す */
  const somePrintSelected = printList.length > 0 && !allPrintSelected;

  /**
   * 10名ずつの「紙」に区切る。
   *
   * 流し込みに任せず自分で区切るのは、**どの紙でもカードの位置を同じにするため**。
   * 位置が揃っていないと、まとめて重ねて裁断できない。
   */
  const sheets: PunchQrEntry[][] = [];
  for (let i = 0; i < printList.length; i += CARDS_PER_SHEET) {
    sheets.push(printList.slice(i, i + CARDS_PER_SHEET));
  }
  /** 実際に打刻できる端末の数。「共用端末が有効」だけでは打てる保証にならない */
  const pairedCount = devices.filter((d) => !d.revoked_at && d.paired).length;

  return (
    <div className="kiosk-page" style={s.page}>
      {/* 印刷時はQRカードだけを残す。管理UIが混ざった紙は現場で使えない */}
      <style>{`
        @media print {
          .kiosk-noprint { display: none !important; }
          .kiosk-print-area { display: block !important; }
          /*
           * 画面用の余白を落とす。**これが無いと2列目が次の行へ落ちる**——
           * カード2枚で 182mm ちょうどを使うので、body の既定余白(8px)や
           * ページの padding が数mmでも足されると幅が足りなくなる
           */
          body { margin: 0 !important; }
          .kiosk-page { max-width: none !important; margin: 0 !important; padding: 0 !important; }
          /*
           * 名刺サイズ(91x55mm)を2列x5段。左右14mm は 91mm x 2列 がちょうど
           * 収まる余白で、市販の「A4 10面 名刺用紙」とほぼ同じ割り付けになる。
           * 上下は10mm——11mm にすると 5段(275mm)と印刷可能高が同値になり、
           * 端数の丸めで最終段が次ページへこぼれて白紙が出る。2mm を逃がしてある。
           */
          @page { size: A4 portrait; margin: 10mm 14mm; }
        }
        .kiosk-print-area { display: none; }
      `}</style>

      <div className="kiosk-noprint">
        <div style={s.header}>
          <div>
            <h1 style={s.h1}>打刻用共用端末</h1>
            <p style={s.sub}>店舗に置いた1台を全員で使う打刻の設定</p>
          </div>
          <button onClick={() => navigate('/dashboard')} style={s.backBtn}>ダッシュボードに戻る</button>
        </div>

        {error && <div style={s.error}>{error}</div>}

        {/* 発行直後のコード */}
        {issuedCode && (
          <section style={{ ...s.card, border: '2px solid #2563EB' }}>
            <h2 style={s.h2}>ペアリングコード（{issuedCode.name}）</h2>
            <p style={s.code}>{issuedCode.code}</p>

            <p style={s.note}>
              <strong>この画面を閉じると再表示できません。</strong>
              必要になったら「コード再発行」で出し直せます。
            </p>

            <p style={{ ...s.note, marginTop: 12 }}>
              打刻に使う端末で次のURLを開き、<strong>30分以内</strong>にコードを入力してください。
            </p>
            {/*
              設置する人はこれを見て端末に打ち込む。パスだけだと分からないので、
              いま開いているホストから完全なURLを組み立てて出す
              （dev / 本番でホストが違うため、固定値では書けない）。
            */}
            <div style={s.url}>{window.location.origin}/kiosk/setup</div>

            <p style={{ ...s.note, color: '#B45309', marginTop: 12 }}>
              ⚠️ 端末の登録は、その端末のブラウザに保存されます。
              <strong>Cookie を削除すると登録が外れ、コードの再発行と再入力が必要になります。</strong>
              共用端末では「終了時に Cookie を削除する」設定を切り、
              プライベート／シークレットモードでは開かないでください。
            </p>

            <button onClick={() => setIssuedCode(null)} style={{ ...s.smallBtn, marginTop: 12 }}>
              閉じる
            </button>
          </section>
        )}

        {/* 端末一覧 */}
        <section style={s.card}>
          <h2 style={s.h2}>端末</h2>

          <form onSubmit={addDevice} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="設置場所がわかる名前（例: 厨房入口タブレット）"
              maxLength={100}
              style={s.input}
            />
            <button type="submit" disabled={!newName.trim()} style={s.primaryBtn}>端末を追加</button>
          </form>

          {devices.length === 0 ? (
            <p style={s.note}>まだ端末がありません。上のフォームから追加してください。</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>名前</th>
                    <th style={s.th}>状態</th>
                    <th style={s.th}>最終通信</th>
                    <th style={s.th}>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {devices.map((d) => (
                    <tr key={d.id} style={{ opacity: d.revoked_at ? 0.5 : 1 }}>
                      <td style={s.td}>{d.name}</td>
                      <td style={s.td}>
                        {d.revoked_at ? '失効' : d.paired ? '登録済み' : d.pairing_pending ? 'コード発行中' : '未登録'}
                      </td>
                      <td style={s.td}>{fmt(d.last_seen_at)}</td>
                      <td style={s.td}>
                        {!d.revoked_at && (
                          <>
                            <button onClick={() => reissue(d)} style={s.smallBtn}>コード再発行</button>
                            <button onClick={() => revoke(d)} style={{ ...s.smallBtn, background: '#DC2626' }}>失効</button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {revokedCount > 0 && (
            <p style={{ ...s.note, marginTop: 12 }}>
              <button onClick={() => setShowRevoked(!showRevoked)} style={s.linkBtn}>
                {showRevoked
                  ? '失効した端末を隠す'
                  : `失効した端末を表示（${revokedCount}台）`}
              </button>
              {' '}失効した端末は一覧から外れますが、記録は残ります
              （過去の打刻がどの端末からかを追えるようにするため）。
            </p>
          )}

          <p style={{ ...s.note, marginTop: 12 }}>
            打刻の有効/無効は<strong>企業設定</strong>の「打刻設定」で変更できます。
          </p>
        </section>

        {/* 打刻用QR */}
        <section style={s.card}>
          <h2 style={s.h2}>打刻用QRコード</h2>
          <p style={s.note}>
            スマートフォンを持つ従業員は、自分の画面にQRを表示できます（ダッシュボードから）。
            <strong>持たない従業員には、ここで発行して印刷したものを配ってください。</strong>
          </p>
          <p style={{ ...s.note, color: '#B45309' }}>
            ⚠️ <strong>印刷し直すだけなら再発行は不要です。</strong>
            発行済みのQRは何度でもそのまま印刷できます。
            再発行すると、その人に配布済みのカードとスマホ表示が
            <strong>その場で使えなくなります</strong>。紛失・複製の疑いがあるときだけ使ってください。
          </p>

          <div style={{ overflowX: 'auto', marginTop: 12 }}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>
                    {/*
                      全選択・全解除。一部だけ選ばれているときは indeterminate に
                      する——チェックが外れて見えると「全部外れている」と読めてしまう
                    */}
                    <input
                      type="checkbox"
                      checked={allPrintSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = somePrintSelected;
                      }}
                      onChange={toggleAllPrint}
                      disabled={issued.length === 0}
                      aria-label={allPrintSelected ? '印刷対象を全て外す' : '発行済みを全て印刷対象にする'}
                      title={allPrintSelected ? '全て外す' : '発行済みを全て選ぶ'}
                    />
                    <span style={{ marginLeft: 6 }}>印刷</span>
                  </th>
                  <th style={s.th}>社員番号</th>
                  <th style={s.th}>氏名</th>
                  <th style={s.th}>状態</th>
                  <th style={s.th}>操作</th>
                </tr>
              </thead>
              <tbody>
                {qrs.map((q) => (
                  <tr key={q.userId}>
                    <td style={s.td}>
                      <input
                        type="checkbox"
                        checked={printIds.has(q.userId)}
                        onChange={() => togglePrint(q.userId)}
                        disabled={!q.qrToken}
                        aria-label={`${q.name} を印刷対象にする`}
                      />
                    </td>
                    <td style={s.td}>{q.employeeNumber || '—'}</td>
                    <td style={s.td}>{q.name}</td>
                    <td style={s.td}>
                      {q.qrToken ? (
                        <span style={{ color: '#166534' }}>
                          発行済み
                          {q.issuedAt && (
                            <span style={{ color: '#6c757d', fontSize: 12 }}>
                              {' '}（{new Date(q.issuedAt).toLocaleDateString('ja-JP')}）
                            </span>
                          )}
                        </span>
                      ) : (
                        <span style={{ color: '#6c757d' }}>未発行</span>
                      )}
                    </td>
                    <td style={s.td}>
                      <button
                        onClick={() => issueQr(q)}
                        disabled={issuingFor === q.userId}
                        style={q.qrToken ? { ...s.smallBtn, background: '#B45309' } : s.smallBtn}
                      >
                        {issuingFor === q.userId ? '処理中…' : q.qrToken ? '再発行' : 'QRを発行'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {issued.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <button onClick={printQrs} disabled={printList.length === 0} style={{
                ...s.primaryBtn,
                opacity: printList.length === 0 ? 0.5 : 1,
                cursor: printList.length === 0 ? 'not-allowed' : 'pointer',
              }}>
                選んだ {printList.length} 件を印刷
              </button>
              <p style={s.note}>
                A4 1枚に <strong>10名分</strong>（名刺サイズ 91×55mm）が並びます。
                {printList.length > 0 && <>選択中は <strong>{sheets.length}枚</strong>。</>}
                破線で切り分けると名刺入れ・パスケースにそのまま入ります。何度でも刷り直せます。
              </p>
              {/*
                ここを書いておかないと現場で必ずズレる。ブラウザの既定は
                ヘッダー・フッターが有効で、そのぶん本文が縮められる
              */}
              <p style={{ ...s.note, color: '#B45309' }}>
                ⚠️ 印刷画面で <strong>用紙=A4・向き=縦・倍率=100%</strong> にし、
                <strong>「ヘッダーとフッター」のチェックを外して</strong>ください。
                付いたままだと本文が縮小され、カードが名刺サイズになりません。
              </p>
            </div>
          )}
        </section>
      </div>

      {/* 印刷専用。切り分けて配るためのカード(名刺サイズ・設計書 §13-8) */}
      <div className="kiosk-print-area">
        {sheets.map((sheet, sheetIndex) => (
          <div
            key={sheetIndex}
            style={{
              ...s.printSheet,
              // 最後の1枚のあとで改ページすると白紙が出る
              breakAfter: sheetIndex === sheets.length - 1 ? 'auto' : 'page',
            }}
          >
            {sheet.map((q) => (
              <div key={q.userId} style={s.printCard}>
                <QrCode value={q.qrToken!} size="40mm" title={`${q.name} の打刻用QRコード`} />
                <div style={s.printBody}>
                  {user?.companyName && <div style={s.printCompany}>{user.companyName}</div>}
                  <div style={s.printName}>{q.name}</div>
                  {q.employeeNumber && <div style={s.printNo}>社員番号 {q.employeeNumber}</div>}
                  <div style={s.printLabel}>打刻用QRコード</div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  page: { maxWidth: 1000, margin: '0 auto', padding: 'clamp(10px, 3vw, 20px)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 16 },
  h1: { margin: 0, fontSize: 'clamp(18px, 4vw, 24px)' },
  h2: { margin: '0 0 8px', fontSize: 'clamp(15px, 3.2vw, 17px)' },
  sub: { margin: '4px 0 0', color: '#6c757d', fontSize: 13 },
  card: { background: '#fff', border: '1px solid #dee2e6', borderRadius: 8, padding: 'clamp(12px, 3vw, 20px)', marginBottom: 16 },
  note: { color: '#6c757d', fontSize: 13, lineHeight: 1.7, margin: '4px 0 0' },
  error: { background: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb', borderRadius: 4, padding: 10, marginBottom: 12 },
  code: { fontSize: 'clamp(32px, 8vw, 44px)', fontWeight: 800, letterSpacing: '0.3em', textAlign: 'center', margin: '12px 0', fontFamily: 'monospace' },
  url: {
    fontFamily: 'monospace',
    fontSize: 'clamp(13px, 2.8vw, 16px)',
    backgroundColor: '#f6f8fa',
    border: '1px solid #d0d7de',
    borderRadius: 6,
    padding: '10px 14px',
    // 設置作業でコピーされることがあるので、選択しやすくしておく
    userSelect: 'all',
    wordBreak: 'break-all',
  },
  input: { flex: '1 1 260px', minWidth: 200, padding: '8px 12px', border: '1px solid #ced4da', borderRadius: 4, fontSize: 14 },
  primaryBtn: { background: '#2563EB', color: '#fff', border: 'none', borderRadius: 4, padding: '8px 18px', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  smallBtn: { background: '#6c757d', color: '#fff', border: 'none', borderRadius: 4, padding: '5px 10px', fontSize: 12, cursor: 'pointer', marginRight: 6 },
  backBtn: { background: '#6c757d', color: '#fff', border: 'none', borderRadius: 4, padding: '8px 14px', fontSize: 13, cursor: 'pointer' },
  toggle: { color: '#fff', border: 'none', borderRadius: 999, padding: '10px 28px', fontSize: 15, fontWeight: 700, cursor: 'pointer' },
  // 設定どうしの境界をはっきりさせる。罫線が無いと、どの説明が
  // どのトグルのものか読み取れない
  routeList: {
    marginTop: 12,
    border: '1px solid #dee2e6',
    borderRadius: 6,
    overflow: 'hidden',
  },
  routeRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    padding: '16px 18px',
    // 2行目以降に区切り線。最初の行には付けない
    borderTop: '1px solid #dee2e6',
    marginTop: -1,
    backgroundColor: 'white',
  },
  // 説明が長くてもボタンが下へ回り込まないよう、幅を分けて持たせる
  routeText: { flex: '1 1 auto', minWidth: 0 },
  routeAction: { flex: '0 0 auto' },
  routeName: { fontSize: 15, fontWeight: 600, color: '#24292e', marginBottom: 4 },
  linkBtn: {
    background: 'none',
    border: 'none',
    color: '#0969da',
    fontSize: 13,
    cursor: 'pointer',
    textDecoration: 'underline',
    padding: 0,
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #dee2e6', color: '#6c757d', fontSize: 12, whiteSpace: 'nowrap' },
  td: { padding: '8px 10px', borderBottom: '1px solid #f1f3f5' },
  // --- 印刷用。寸法は全て mm で持つ ---
  // px で書くと紙の上の大きさがブラウザの解像度換算に依存し、名刺サイズにならない
  printSheet: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 91mm)',
    gridAutoRows: '55mm',
    width: '182mm',
  },
  printCard: {
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    gap: '3mm',
    padding: '4mm',
    // 切り取り線。隣り合うカードで線が二重になるが、細い破線なので
    // 裁断の邪魔にはならない。濃くすると切り残しが目立つ
    border: '0.2mm dashed #c0c0c0',
    breakInside: 'avoid',
    overflow: 'hidden',
  },
  printBody: { minWidth: 0, flex: '1 1 auto' },
  // 拾われたときに、どこの何なのか分かるようにしておく
  printCompany: { fontSize: '2.8mm', color: '#555', marginBottom: '1mm', overflowWrap: 'anywhere' },
  printName: { fontSize: '5mm', fontWeight: 700, lineHeight: 1.25, overflowWrap: 'anywhere' },
  printNo: { fontSize: '3mm', color: '#555', marginTop: '1mm' },
  printLabel: { fontSize: '2.6mm', color: '#888', marginTop: '2mm' },
};

export default KioskDevicesPage;
